import React from 'react'

import {
  TextField,
  CircularProgress,
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  Typography,
  Checkbox,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Autocomplete from '@mui/material/Autocomplete'

import { useAutocomplete } from 'shared/hooks/useAutocomplete'
import { formatStockQuantity } from 'components/inventory/helpers'


const DEFAULT_PAGE_SIZE = 50

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    productRow: {
      cursor: 'pointer',
    },
  })
)

interface header {
  id: string
  numeric: boolean
  disablePadding: boolean
  label: string
  width: string
  isCheckbox?: boolean
  isPrice?: boolean
}

export default function BaseAutocomplete<T>(props: {
  name?: string
  onChange?: (data: T) => void
  onInputChange?: (data: string, reason: string) => void
  brandId?: string
  disabled?: boolean
  required?: boolean
  showInactive?: boolean
  getMatchedData?: (data: T[]) => void
  showDetailedTable?: boolean
  loadAll?: boolean
  url: string
  headers: header[]
  label: string
  responseMapper: (response: T) => string
  loadSalesOrderDisplayId?: boolean
  loadByProductName?: boolean
  loadBySearch?: boolean
  extraParams?: Record<string, string>
}) {
  const {
    onChange,
    onInputChange,
    name,
    disabled,
    brandId,
    showInactive,
    getMatchedData,
    showDetailedTable,
    loadAll,
    url,
    headers,
    label,
    responseMapper,
    loadSalesOrderDisplayId,
    extraParams,
    loadByProductName,
    loadBySearch,
  } = props
  const classes = useStyles()
  const {
    options,
    localData,
    isLoading,
    tempText,
    setTempText,
    isUserTyping,
    setIsUserTyping,
    loadAutocomplete
  } = useAutocomplete({
    url,
    name,
    loadAll,
    showInactive,
    showDetailedTable,
    extraParams,
    brandId,
    responseMapper,
    getMatchedData,
    onInputChange,
    loadSalesOrderDisplayId,
    loadByProductName,
    loadBySearch
  })

  const [page, setPage] = React.useState(0)

  const filteredData = React.useMemo(() => {
    if (!loadAll || !isUserTyping || !tempText.trim()) return localData
    const input = tempText.trim().toLowerCase()
    return localData.filter((field) =>
      headers.some((h) =>
        String((field as any)[h.id] ?? '').toLowerCase().includes(input)
      )
    )
  }, [localData, tempText, isUserTyping, loadAll, headers])

  const getDisplayText = (row: any, header: header) => {
    if (header.isPrice) {
      return (
        <Typography>
          {/* @ts-ignore */}
          Rp.{new Intl.NumberFormat('en-US').format(Number(row[header.id]))}
        </Typography>
      )
    }
    if (header.numeric || header.id === 'stock') {
      // @ts-ignore
      const val = row[header.id]
      if (val == null) return ''
      return <Typography>{formatStockQuantity(val)}</Typography>
    }
    if (header.isCheckbox) {
      // @ts-ignore
      return <Checkbox disabled checked={row[header.id]} />
    }

    // @ts-ignore
    return row[header.id]
  }
  const selectedValue = isUserTyping
    ? null
    : (options &&
      options.find((opt) =>
        name ? String(opt).startsWith(String(name)) : false
      )) ||
    (tempText && options && options.includes(tempText) ? tempText : null) ||
    name ||
    ''
  return (
    <>
      {!showDetailedTable && (
        <Autocomplete
          disabled={disabled}
          fullWidth
          clearOnBlur={false}
          filterOptions={(opts, state) => {
            if (!loadAll) {
              return opts
            }
            const input = String(state.inputValue || '').trim().toLowerCase()
            if (!input) {
              return opts
            }
            return opts.filter((opt) =>
              String(opt).toLowerCase().includes(input)
            )
          }}
          onClose={() => {
            // Keep the text on close/blur to prevent unintended resetting of the input
          }}
          onChange={(_: React.ChangeEvent<{}>, selectedData: string | null) => {
            const matchingData = localData.find(
              (singleRow) => responseMapper(singleRow) === selectedData
            )
            // fallback: if matchingData not found, try matching by id part (before ' - ')
            let result = matchingData
            if (!result && selectedData) {
              const idPart = String(selectedData).split(' - ')[0]
              const idKey =
                headers && headers.length ? headers[0].id : undefined
              if (idKey) {
                // @ts-ignore
                result = localData.find((row) => String(row[idKey]) === idPart)
              }
            }
            if (result) {
              onChange && onChange(result)
            } else if (!selectedData) {
              onChange && onChange(null as any)
            }
            // preserve the displayed text after selection so the parent's id prop
            // update doesn't flip the input and retrigger a load
            setTempText(selectedData || '')
            setIsUserTyping(false)
          }}
          // Display the full mapped option when possible so value doesn't show incomplete id or 'undefined'
          value={selectedValue}
          options={options}
          loading={isLoading}
          inputValue={tempText || ''}
          onInputChange={(_, newInputValue, reason) => {
            onInputChange?.(newInputValue, reason)
            if (reason === 'input') {
              setTempText(newInputValue)
              setIsUserTyping(true)
            } else if (reason === 'clear') {
              setTempText('')
              setIsUserTyping(false)
            } else if (reason === 'reset') {
              if (!isUserTyping) {
                setTempText(newInputValue)
              }
            }
            if (newInputValue === '') {
              loadAutocomplete('')
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      )}
      {showDetailedTable && (
        <Box>
          <TextField
            fullWidth
            label={label}
            variant="outlined"
            value={tempText || name}
            onChange={(e) => {
              setTempText(e.target.value)
              setIsUserTyping(true)
              setPage(0)
            }}
            InputProps={{
              endAdornment: isLoading ? (
                <InputAdornment position="end">
                  <CircularProgress color="inherit" size={20} />
                </InputAdornment>
              ) : null,
            }}
          />

          <Box>
            {filteredData.length > DEFAULT_PAGE_SIZE && (
              <TablePagination
                count={filteredData.length}
                rowsPerPage={DEFAULT_PAGE_SIZE}
                rowsPerPageOptions={[]}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
              />
            )}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {headers.map((headCell) => (
                      <TableCell
                        key={headCell.id + 'header'}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                      >
                        {headCell.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(
                      page * DEFAULT_PAGE_SIZE,
                      (page + 1) * DEFAULT_PAGE_SIZE
                    )
                    .map((field) => (
                      <TableRow
                        hover
                        onClick={() => {
                          onChange && onChange(field)
                          setIsUserTyping(false)
                          // Don't clear the input when using the detailed table mode
                          // so the user's search/filtering remains visible after
                          // selecting a row (the dialog or downstream UI may open).
                          if (!showDetailedTable) {
                            setTempText('')
                          }
                        }}
                        role="checkbox"
                        className={classes.productRow}
                      >
                        {headers.map((header) => (
                          <TableCell>{getDisplayText(field, header)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}
    </>
  )
}
